<script setup>
import { ref, reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import MarketingCampaignsAPI from 'dashboard/api/marketingCampaigns';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Policy from 'dashboard/components/policy.vue';

// Create / edit an attribution code. Opens as a side panel like every other
// form of the redesign (Dialog decides the presentation).
const props = defineProps({
  sources: { type: Array, default: () => [] },
});

const emit = defineEmits(['saved', 'deleted']);

const { t } = useI18n();

const dialogRef = ref(null);
const deleteDialogRef = ref(null);
const isSaving = ref(false);
const isDeleting = ref(false);
const codeTouched = ref(false);

const state = reactive({
  id: null,
  code: '',
  description: '',
  source: '',
  medium: '',
  campaign: '',
  term: '',
  content: '',
});

const isEdit = computed(() => !!state.id);
const title = computed(() =>
  isEdit.value
    ? t('ATTRIBUTION.DIALOG.EDIT_TITLE')
    : t('ATTRIBUTION.DIALOG.CREATE_TITLE')
);

// The code must not be purely numeric — the model rejects that.
const codeError = computed(() => {
  if (!codeTouched.value) return '';
  if (!state.code.trim()) return t('ATTRIBUTION.DIALOG.CODE_REQUIRED');
  if (/^\d+$/.test(state.code.trim()))
    return t('ATTRIBUTION.DIALOG.CODE_NUMERIC');
  return '';
});

const sourceOptions = computed(() => {
  const names = props.sources.map(source => source.name).filter(Boolean);
  return names.length ? names : ['facebook', 'instagram', 'google', 'tiktok'];
});

const MEDIUM_OPTIONS = ['ppc', 'paid_social', 'organic', 'referral', 'email'];

// New codes get the next free code for the chosen source (f1, f2, …).
const suggestCode = async () => {
  try {
    const { data } = await MarketingCampaignsAPI.getNextCode(state.source);
    if (!isEdit.value && data?.next_code) state.code = data.next_code;
  } catch {
    // Sin sugerencia: el usuario escribe el código a mano.
  }
};

const open = (campaign = null) => {
  Object.assign(state, {
    id: campaign?.id ?? null,
    code: campaign?.code ?? '',
    description: campaign?.description ?? '',
    source: campaign?.utms?.source ?? sourceOptions.value[0],
    medium: campaign?.utms?.medium ?? 'ppc',
    campaign: campaign?.utms?.campaign ?? '',
    term: campaign?.utms?.term ?? '',
    content: campaign?.utms?.content ?? '',
  });
  codeTouched.value = false;
  dialogRef.value?.open();
  if (!campaign) suggestCode();
};
const close = () => dialogRef.value?.close();

const onSourceChange = () => {
  if (!isEdit.value) suggestCode();
};

const payload = () => ({
  marketing_campaign: {
    code: state.code.trim(),
    description: state.description.trim(),
    utms: {
      source: state.source,
      medium: state.medium,
      campaign: state.campaign.trim(),
      term: state.term.trim(),
      content: state.content.trim(),
    },
  },
});

const save = async () => {
  codeTouched.value = true;
  if (codeError.value) return;
  isSaving.value = true;
  try {
    if (isEdit.value) {
      await MarketingCampaignsAPI.update(state.id, payload());
      useAlert(t('ATTRIBUTION.DIALOG.UPDATE_SUCCESS'));
    } else {
      await MarketingCampaignsAPI.create(payload());
      useAlert(t('ATTRIBUTION.DIALOG.CREATE_SUCCESS'));
    }
    emit('saved');
    close();
  } catch (error) {
    useAlert(error?.response?.data?.message || t('ATTRIBUTION.DIALOG.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const openDeleteConfirm = () => deleteDialogRef.value?.open();

const remove = async () => {
  isDeleting.value = true;
  try {
    await MarketingCampaignsAPI.delete(state.id);
    useAlert(t('ATTRIBUTION.DIALOG.DELETE_SUCCESS'));
    deleteDialogRef.value?.close();
    close();
    emit('deleted');
  } catch (error) {
    useAlert(error?.response?.data?.message || t('ATTRIBUTION.DIALOG.ERROR'));
  } finally {
    isDeleting.value = false;
  }
};

defineExpose({ open, close });

const LABEL_CLASS = 'text-[11.5px] font-semibold text-n-slate-11';
const FIELD_CLASS =
  'w-full h-[34px] px-2.5 rounded-lg border border-n-weak bg-n-alpha-1 text-[13px] text-n-slate-12 placeholder:text-n-slate-10 outline-none focus:bg-n-solid-2 focus:border-n-brand/60 transition-colors reset-base';
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="title"
    :description="t('ATTRIBUTION.DIALOG.DESCRIPTION')"
    :confirm-button-label="
      isEdit ? t('ATTRIBUTION.DIALOG.SAVE') : t('ATTRIBUTION.DIALOG.CREATE')
    "
    :is-loading="isSaving"
    :disable-confirm-button="isSaving"
    @confirm="save"
  >
    <div class="flex flex-col gap-4">
      <div class="grid grid-cols-2 gap-x-3.5 gap-y-3">
        <label class="flex flex-col gap-1.5">
          <span :class="LABEL_CLASS">{{ t('ATTRIBUTION.DIALOG.CODE') }}</span>
          <input
            v-model="state.code"
            class="font-mono"
            :class="[FIELD_CLASS, codeError ? '!border-n-ruby-8' : '']"
            :placeholder="t('ATTRIBUTION.DIALOG.CODE_PLACEHOLDER')"
            @blur="codeTouched = true"
          />
          <span v-if="codeError" class="text-xs text-n-ruby-11">{{
            codeError
          }}</span>
        </label>
        <label class="flex flex-col gap-1.5">
          <span :class="LABEL_CLASS">{{
            t('ATTRIBUTION.BUILDER.SOURCE')
          }}</span>
          <select
            v-model="state.source"
            :class="FIELD_CLASS"
            @change="onSourceChange"
          >
            <option
              v-for="source in sourceOptions"
              :key="source"
              :value="source"
            >
              {{ source }}
            </option>
          </select>
        </label>
        <label class="flex flex-col gap-1.5">
          <span :class="LABEL_CLASS">{{
            t('ATTRIBUTION.BUILDER.MEDIUM')
          }}</span>
          <select v-model="state.medium" :class="FIELD_CLASS">
            <option
              v-for="medium in MEDIUM_OPTIONS"
              :key="medium"
              :value="medium"
            >
              {{ medium }}
            </option>
          </select>
        </label>
        <label class="flex flex-col gap-1.5">
          <span :class="LABEL_CLASS">{{ t('ATTRIBUTION.BUILDER.TERM') }}</span>
          <input
            v-model="state.term"
            :class="FIELD_CLASS"
            :placeholder="t('ATTRIBUTION.DIALOG.TERM_PLACEHOLDER')"
          />
        </label>
      </div>

      <label class="flex flex-col gap-1.5">
        <span :class="LABEL_CLASS">{{
          t('ATTRIBUTION.BUILDER.CAMPAIGN')
        }}</span>
        <input
          v-model="state.campaign"
          :class="FIELD_CLASS"
          :placeholder="t('ATTRIBUTION.DIALOG.CAMPAIGN_PLACEHOLDER')"
        />
      </label>

      <label class="flex flex-col gap-1.5">
        <span :class="LABEL_CLASS">{{ t('ATTRIBUTION.BUILDER.CONTENT') }}</span>
        <input
          v-model="state.content"
          :class="FIELD_CLASS"
          :placeholder="t('ATTRIBUTION.DIALOG.CONTENT_PLACEHOLDER')"
        />
      </label>

      <label class="flex flex-col gap-1.5">
        <span :class="LABEL_CLASS">{{ t('ATTRIBUTION.DIALOG.NOTE') }}</span>
        <input
          v-model="state.description"
          :class="FIELD_CLASS"
          :placeholder="t('ATTRIBUTION.DIALOG.NOTE_PLACEHOLDER')"
        />
      </label>

      <Policy v-if="isEdit" :permissions="['administrator']">
        <div class="flex justify-start">
          <Button
            type="button"
            :label="t('ATTRIBUTION.DIALOG.DELETE')"
            icon="i-lucide-trash"
            color="ruby"
            variant="link"
            size="sm"
            @click="openDeleteConfirm"
          />
        </div>
      </Policy>
    </div>
  </Dialog>

  <Dialog
    ref="deleteDialogRef"
    type="alert"
    :title="t('ATTRIBUTION.DIALOG.DELETE_CONFIRM.TITLE')"
    :description="t('ATTRIBUTION.DIALOG.DELETE_CONFIRM.DESCRIPTION')"
    :confirm-button-label="t('ATTRIBUTION.DIALOG.DELETE_CONFIRM.CONFIRM')"
    :is-loading="isDeleting"
    :disable-confirm-button="isDeleting"
    @confirm="remove"
  />
</template>
